/* eslint-disable no-use-before-define */
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { Col, Row } from '@bingads-webui-react/primitive';
import { BaseAccountBusinessLocation, Forms, requiredFeatures as ablFeatures } from '@bingads-webui-cc-react/account-business-location';
import { withClientCenterApiContext } from './clientcenter-api-context';
import { withTelemetry, useTelemetry } from './use-telemetry';
import { BusinessDetailsPrimaryUse, isPrimaryUseSelected, PrimaryPurpose } from './business-details-primary-use-edit';
import useScope from './use-scope';
import useFeatures from './use-features';
import useBusinessInfoI18nContext from './business-info-setup/use-i18n-context';
import useDefaults from './business-info-setup/use-defaults';
import useDataRestrictions from './business-info-setup/use-data-restrictions';
import useChangeHandlers from './business-info-setup/use-change-handlers';
import useLazyState from './use-lazy-state';
import TermsAndConditions from './terms-and-conditions';
import LifecycleEvent from './lifecycle-event';

export default withTelemetry('BusinessInfoSetup', withClientCenterApiContext(BusinessInfoSetup));

function getInitialPrimaryUse(savedPrimaryUse, isPrimaryUseSaved, showAgencyIdentification) {
  if (showAgencyIdentification) {
    return isPrimaryUseSaved && isPrimaryUseSelected(savedPrimaryUse) ? savedPrimaryUse : '';
  }

  return savedPrimaryUse || PrimaryPurpose.SelfAdvertising;
}

/**
 * Defines a component for setting up an account's business location (a.k.a. ABL) and information during customer onboarding.
 * @param {string} props.className The CSS class name to apply to the component.
 * @param {object} props.controls Hints on how to render the controls on the form.
 * The country and currency fields can be hidden or disabled; the T&C control can be hidden.
 * @param {object} props.init The default values to use for the component.
 * Defaults for the following controls are supported: "name", "country", "currency", "phone" and "address".
 * If present, the values are used only during form initialization;
 * otherwise the form will attempt to load and find suitable defaults by itself.
 * @param {object} props.dataRestrictions Allows imposing custom restrictions on the domain of valid values for certain controls.
 * Custom restrictions can be placed on phone, time zone, country and currency controls.
 * Otherwise implicit, default restrictions will be used.
 * @param {function} props.onStateChange The callback to invoke when the component's state changes.
 * The function will receive an argument with the following properties:
 * "type": The type of change that occurred;
 * one of "CHANGE_DATA", "DONE_LOADING", "DONE_SAVING", "FAILED_LOADING" and "FAILED_SAVING".
 * "args": The optional arguments associated with the state change.
 * For failure events, this will be an object with an "error" property.
 * For change events, this will be an object with a "data" property;
 * if validation was run and it passed, an additional "isValid" property will be present.
 * @param {function} props.setSaveHandler The callback to invoke when setting the logic for saving form data.
 * This allows the trigger for saving the data to be outside the component, while the actual handling can be done inside.
 * The page hosting the component is expected to save the specified handler, and call it when the Save button is clicked.
 * @param {function} props.setReloadHandler The callback to invoke when setting the logic for reloading form data.
 * This allows the host to reload *some* of the initialization data when the previous load attempt failed;
 * it will have no impact if the form is already initialized.
 * The page hosting the component is expected to save the specified handler,
 * and call it, after a failed initialization,  when the Reload button is clicked.
 * @param {React.ReactNode} props.contentAfterCurrencySelector Content rendered immediately after the currency selector.
 * @returns {JSX.Element} The form to set up the account details.
 */
function BusinessInfoSetup({
  className,
  controls,
  init,
  dataRestrictions: {
    phoneByCountry: restrictPhoneByCountry,
    timeZoneByCountry: restrictTimeZoneByCountry,
    countries: countryFilter,
    currencies: currencyFilter,
    currencyCountriesMapping: currencyCountriesMappingOverride,
    subGeography,
    account: accountOverride,
    userAddressSubGeography,
    address: addressOverride,
  },
  onStateChange,
  setSaveHandler,
  setReloadHandler,
  isPrimaryUseSaved,
  onPrimaryUseSaved,
  overrideTargetCountry,
  contentAfterCurrencySelector,
}) {
  const scope = useScope();
  const i18n = useBusinessInfoI18nContext();
  const logger = useBusinessInfoLogger();
  const features = useBusinessInfoFeatures();
  const formControls = useControls(controls);

  // TODO: check if the account is in draft state; skip otherwise
  const prefetchedEntities = useMemo(() => {
    if (!accountOverride) {
      return undefined;
    }
    return { account: accountOverride };
  }, [accountOverride]);
  const defaults = useDefaults({ scope, initializers: init, prefetchedEntities });

  const savedPrimaryUse = defaults.baseline.customer && defaults.baseline.customer.PrimaryPurpose;
  const initialPrimaryUse = getInitialPrimaryUse(
    savedPrimaryUse,
    isPrimaryUseSaved,
    controls.showAgencyIdentification
  );
  const [selectedPrimaryUse, setSelectedPrimaryUse] = useState('');
  const effectivePrimaryUse = selectedPrimaryUse || initialPrimaryUse;

  // TODO: if currency is hidden or disabled, make sure to restrict the country options accordingly
  const dataRestrictions = useDataRestrictions({
    phoneByCountry: restrictPhoneByCountry,
    timeZoneByCountry: restrictTimeZoneByCountry,
    countries: countryFilter,
    currencies: currencyFilter,
    currencyCountriesMapping: currencyCountriesMappingOverride,
    subGeography,
    account: accountOverride,
    userAddressSubGeography,
    address: addressOverride,
  });

  const [currentCountry, setCurrentCountry, currentCountrySettled] = useLazyState(defaults.data.countryCode, !defaults.isLoading);
  const updateLocalStateAndRelayEvent = useCallback((event) => {
    onStateChange(event);
    if (event.type === LifecycleEvent.DataChanged && event.args.data.country != null) {
      setCurrentCountry(event.args.data.country);
    }
  }, [onStateChange, setCurrentCountry]);

  const {
    onFormChange,
    onTermsChange,
    onPrimaryUseChange,
    checkAgreement,
    onValidationDone,
  } = useChangeHandlers({
    scope,
    baseline: defaults.baseline,
    requiresAgreement: !controls.hideTerms,
    requiresPrimaryUse: controls.showBusinessDetailsPrimaryUse,
    requiresExplicitPrimaryUse: controls.showAgencyIdentification,
    initialPrimaryUse,
    onPrimaryUseSaved,
    onStateChange: updateLocalStateAndRelayEvent,
    overrideTargetCountry,
  });

  const preCheckBeforeValidation = useCallback(() => {
    let agreementCheck = Promise.resolve();
    let primaryUseCheck = Promise.resolve();

    if (!controls.hideTerms) {
      agreementCheck = checkAgreement;
    }

    if (controls.showAgencyIdentification && !isPrimaryUseSelected(effectivePrimaryUse)) {
      primaryUseCheck = Promise.reject();
    }

    return Promise.all([agreementCheck, primaryUseCheck]);
  }, [checkAgreement, controls.hideTerms, controls.showAgencyIdentification, effectivePrimaryUse]);

  const onPrimaryUseSelected = useCallback((value) => {
    setSelectedPrimaryUse(value);
    onPrimaryUseChange(value);
  }, [onPrimaryUseChange]);

  // NOTE: triggering the validation will eventually result in the form data being saved, if everything checks out
  const { setValidator, saveAttempted } = useSaveHandler(setSaveHandler, preCheckBeforeValidation, updateLocalStateAndRelayEvent);

  useReloadHandler(setReloadHandler);
  const setIsLoadingFormData = useLoadEvents(defaults.isLoading, defaults.hasErrors() && defaults.errors, onStateChange);

  return (
    <div className={classNames(className, 'business-info-setup')}>
      <Row>
        <Col sm={12}>
          {!defaults.isLoading && !defaults.hasErrors() && <BaseAccountBusinessLocation
            i18n={i18n}
            customerId={scope.cid}
            accountId={scope.aid}
            setLoading={setIsLoadingFormData}
            dataManagement={dataRestrictions}
            formControls={formControls}
            initialAblInfo={defaults.data}
            getTriggerValidationFunction={setValidator}
            moveForward={onValidationDone}
            onAblInfoDataChanged={onFormChange}
            notificationApi={notificationApi}
            logger={logger}
            permissions={features}
            excludeCustomerUnsupportedBusinessLocations
            excludeUnsupportedBusinessLocations
            disableCurrencyAutoSelect={controls.disableCurrencyAutoSelect}
            contentAfterCurrencySelector={contentAfterCurrencySelector}
          />}
        </Col>
      </Row>
      {controls.showBusinessDetailsPrimaryUse &&
        <BusinessDetailsPrimaryUse
          selectedPrimaryUse={effectivePrimaryUse}
          setSelectedPrimaryUse={onPrimaryUseSelected}
          showAgencyIdentification={controls.showAgencyIdentification}
          showValidationResult={controls.showAgencyIdentification && saveAttempted && !isPrimaryUseSelected(effectivePrimaryUse)}
        />
      }
      {!controls.hideTerms && currentCountrySettled && currentCountry != null && <TermsAndConditions country={currentCountry} onChange={onTermsChange} showValidationResult={saveAttempted} />}
    </div>
  );
}

// extract all the features/pilots required by the ABL component
function useBusinessInfoFeatures() {
  const features = useFeatures(_.values(ablFeatures)).objects;

  return useMemo(() => ({
    dynamic: {
      ..._.mapObject(features, f => f.isEnabled),
    },
  }), [features]);
}

function useControls(controls) {
  return useMemo(() => ({
    [Forms.BusinessAddressSelector]: {
      visible: false,
    },
    [Forms.PhoneNumber]: {
      visible: true,
    },
    [Forms.TimeZone]: {
      visible: true,
    },
    [Forms.CountrySelector]: {
      visible: controls.country !== 'hidden',
      disabled: controls.country === 'disabled',
    },
    [Forms.CurrencySelector]: {
      visible: controls.currency !== 'hidden',
      disabled: controls.currency === 'disabled',
    },
    [Forms.UserLocation]: {
      visible: true,
    },
    [Forms.UserAddressForm]: {
      visible: true,
    },
  }), [controls.currency, controls.country]);
}

function useSaveHandler(setSaveHandler, preCheckBeforeValidation, onStateChange) {
  const [saveAttempted, setSaveAttempted] = useState(false);
  const validator = useRef(_.noop);
  const setValidator = useCallback((v) => {
    validator.current = v;
  }, []);

  useEffect(() => {
    setSaveHandler(() => {
      setSaveAttempted(true);

      preCheckBeforeValidation()
        .then(() => {
          validator.current.call();
        })
        .catch(() => {
          onStateChange({ type: LifecycleEvent.SaveFailed, args: { validated: false } });
        });
    });
  }, [setSaveHandler, setSaveAttempted, preCheckBeforeValidation, onStateChange]);

  return { setValidator, saveAttempted };
}

function useReloadHandler(setReloadHandler) {
  const reload = useCallback(() => {
    // TODO: implement reload logic
  }, []);

  useEffect(() => {
    setReloadHandler(reload);
  }, [setReloadHandler, reload]);
}

function useLoadEvents(isLoadingDefaults, loadingErrors, onStateChange) {
  const log = useTelemetry();
  const [doneLoading, setDoneLoading] = useState();
  const [isLoadingFormData, setIsLoadingFormData] = useState();
  // #HACK: from the series "amazing hacks to make simple things work"...
  // we need a signal to tell us when the ABL component has finished loading its initial data;
  // however, the signal that we actually get starts as "false" (not loading),
  // then it realizes "oh, no, I actually have so much to load!", so it flips to "true" (loading),
  // then it keeps flipping on and off, as more data is loaded;
  // as a quick workaround to make some sense of all of this, we'll start in an undefined state,
  // and assume the first signal means "start loading", regardless of the actual value;
  // hopefully by the time it flips to "false" again it loaded enough of the data to be good enough
  // TODO: maybe preload all the essential data ourselves and ignore everything else?
  const setDenoisedLoadSignal = useCallback((value) => {
    setIsLoadingFormData(isLoadingFormData == null || value);
    log.trace(`ABL loading: ${JSON.stringify(value)}`);
  }, [isLoadingFormData, setIsLoadingFormData, log]);

  // NOTE: we need to include form data status when reporting our load status, otherwise we may end up seeing a blank form;
  // on the other hand, form data status moves on and off from the loadig state throughout the lifetime of the form,
  // even confusingly reporting on save status; this means it can't be directly included in our report, as it will render it meaningless;
  // as a workaround, we consider only the initial "loading" as a true loading state, and ignore all subsequent changes
  useEffect(() => {
    if (!doneLoading && !isLoadingDefaults && isLoadingFormData === false) {
      setDoneLoading(true);

      if (loadingErrors) {
        onStateChange({ type: LifecycleEvent.LoadFailed, args: { error: loadingErrors } });
        log.error(`Loading errors: ${JSON.stringify(loadingErrors)}`);
      } else {
        onStateChange({ type: LifecycleEvent.DataLoaded });
        log.trace('Done loading data');
      }
    }
  }, [isLoadingFormData, isLoadingDefaults, loadingErrors, doneLoading, setDoneLoading, onStateChange, log]);

  return setDenoisedLoadSignal;
}

// special log adapter for the ABL component
function useBusinessInfoLogger() {
  const { trace: log } = useTelemetry();

  // TODO: consider if we need a different implementation
  const logger = useMemo(() => ({
    trace: (scenario, message) => log(JSON.stringify({ scenario, message })),
  }), [log]);

  return logger;
}

const CountryPropType = PropTypes.shape({
  Id: PropTypes.number.isRequired,
  Code: PropTypes.string.isRequired,
  Name: PropTypes.string.isRequired,
  Currency: PropTypes.string,
  CountryCallingCode: PropTypes.string,
  IsVatApplicable: PropTypes.bool,
  IsPaperlessBillingAllowed: PropTypes.bool,
  PaperlessBillingEnforced: PropTypes.bool,
});

const CurrencyPropType = PropTypes.shape({
  Id: PropTypes.number.isRequired,
  Code: PropTypes.string.isRequired,
  DisplayName: PropTypes.string.isRequired,
  ControlledCurrencyCountryId: PropTypes.number,
});

const SubGeographyPropType = PropTypes.shape({
  Key: PropTypes.number.isRequired,
  Value: PropTypes.string.isRequired,
});

const ControlPropType = PropTypes.oneOf(['hidden', 'disabled']);
const CountryListPropType = PropTypes.arrayOf(CountryPropType);
const CurrencyListPropType = PropTypes.arrayOf(CurrencyPropType);
const SubGeographyListPropType = PropTypes.arrayOf(SubGeographyPropType);

const InitializerPropType = type => PropTypes.oneOfType([type, PropTypes.shape({
  value: type.isRequired,
  type: PropTypes.oneOf(['default', 'override']),
})]);

BusinessInfoSetup.propTypes = {
  className: PropTypes.string,
  onStateChange: PropTypes.func,
  setReloadHandler: PropTypes.func,
  setSaveHandler: PropTypes.func.isRequired,
  isPrimaryUseSaved: PropTypes.bool,
  onPrimaryUseSaved: PropTypes.func,
  init: PropTypes.shape({
    country: InitializerPropType(PropTypes.string),
    currency: InitializerPropType(PropTypes.string),
    name: InitializerPropType(PropTypes.string),
    phone: InitializerPropType(PropTypes.string),
    address: InitializerPropType(PropTypes.shape({
      Line1: PropTypes.string,
      Line2: PropTypes.string,
      Line3: PropTypes.string,
      Line4: PropTypes.string,
      City: PropTypes.string,
      StateOrProvince: PropTypes.string,
      PostalCode: PropTypes.string,
      Country: PropTypes.string,
    })),
  }),
  dataRestrictions: PropTypes.shape({
    phoneByCountry: PropTypes.bool,
    timeZoneByCountry: PropTypes.bool,
    countries: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(Promise), CountryListPropType]),
    currencies: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(Promise), CurrencyListPropType]),
    currencyCountriesMapping: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    subGeography: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(Promise), SubGeographyListPropType]),
    account: PropTypes.shape({}),
    userAddressSubGeography: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(Promise), SubGeographyListPropType]),
    address: PropTypes.func,
  }),
  controls: PropTypes.shape({
    currency: ControlPropType,
    country: ControlPropType,
    hideTerms: PropTypes.bool,
    showBusinessDetailsPrimaryUse: PropTypes.bool,
    showAgencyIdentification: PropTypes.bool,
    disableCurrencyAutoSelect: PropTypes.bool,
  }),
  overrideTargetCountry: PropTypes.func,
  contentAfterCurrencySelector: PropTypes.node,
};

BusinessInfoSetup.defaultProps = {
  className: null,
  dataRestrictions: {},
  init: {},
  controls: {},
  onStateChange: _.noop,
  setReloadHandler: _.noop,
  isPrimaryUseSaved: false,
  onPrimaryUseSaved: _.noop,
  overrideTargetCountry: undefined,
  contentAfterCurrencySelector: null,
};
